import { ConditionalWrapper, Grid, useDeviceType } from '@packages/shared';
import Container from '@mui/material/Container';
import type { Alignment } from '../../../interfaces';
import { useFilterTimeControl } from '../../utils/filterTimeControl';
import { ComponentRecognizer } from '../ComponentRecognizer';
import type { CmsGroupProps } from './types';
import { registerComponents } from '../ComponentRecognizer/componentRegistry';
import { ComponentTypes } from '../../../interfaces';

export enum CmsGroupTestIds {
  main = 'cms-group',
  item = 'cms-group-item',
  noRenderComponentFound = 'cms-group-no-component-found',
}

const mapVerticalAlignment = (type: Alignment) => {
  if (type === 'top') {
    return 'flex-start';
  }
  if (type === 'bottom') {
    return 'flex-end';
  }
  return type;
};

const textAlign = (horizontalAlignment: Alignment) =>
  horizontalAlignment === 'center' ||
  horizontalAlignment === 'left' ||
  horizontalAlignment === 'right'
    ? {
        textAlign: horizontalAlignment,
      }
    : {};

export const CmsGroup = ({ data, sx }: CmsGroupProps) => {
  const {
    field_cells_per_row: cellsPerRowDesktop = data.paragraph ? data.paragraph.length : 0,
    field_cells_per_row_mobile: cellsPerRowMobile = Math.floor(cellsPerRowDesktop / 2) || 1,
    field_col_gap: colGap = '0',
    field_row_gap: rowGap = '0',
    field_margin: margin = '0',
    field_padding: padding = '0',
    field_background_color: backgroundColor = 'transparent',
    field_alignment: horizontalAlignment = 'center',
    field_vertical_alignment: verticalAlignment = 'flex-start',
    field_custom_id: dynamicYieldId,
    field_distance: distanceType,
  } = data;
  const filterTimeControl = useFilterTimeControl();
  const { isMobile } = useDeviceType();
  const cellsPerRow = isMobile ? cellsPerRowMobile : cellsPerRowDesktop;

  const contentElements = data.paragraph?.filter((item) => {
    if ('field_valid_from' in item || 'field_valid_to' in item) {
      return filterTimeControl(item);
    }
    return item;
  });

  if (contentElements?.length && contentElements?.length > 0) {
    return (
      <Grid
        className="cms-group"
        container
        columns={cellsPerRow}
        columnSpacing={0}
        spacing={0}
        {...(horizontalAlignment && { justifyContent: horizontalAlignment })}
        {...(verticalAlignment && { alignItems: mapVerticalAlignment(verticalAlignment) })}
        {...textAlign(horizontalAlignment)}
        sx={{
          backgroundColor,
          m: margin,
          p: padding,
          width: 'initial', // MUI sets width to 100%, but that won't work with our margin from CMS
          ...sx,
        }}
        data-testid={CmsGroupTestIds.main}
        data-dy={dynamicYieldId}
      >
        {contentElements.map((item) => (
          <Grid
            key={item.id}
            item
            xs={1}
            md={1}
            sx={{
              /* setting MUI's columnSpacing and rowGap on the container is not sufficient and
                 would slightly break the page layouts.
                 We need our own custom tailored column styles here. */
              ...(cellsPerRow > 1 && {
                flexBasis: () => {
                  if (colGap !== '0') {
                    return `calc((100% - ${colGap} * (${cellsPerRow} - 1)) / ${cellsPerRow}) !important`;
                  }
                  return `calc(100% / ${cellsPerRow}) !important`;
                },
              }),
              overflow: 'hidden',
              marginBottom: rowGap,
              marginRight: cellsPerRow > 1 ? colGap : 0,
              ...(cellsPerRow > 1 && {
                [`&:nth-of-type(${cellsPerRow}n+0)`]: {
                  marginRight: '0',
                },
              }),
              '& > .cms-group': { height: '100%' }, // vertically stretch sub groups
            }}
            data-testid={CmsGroupTestIds.item}
          >
            {item && (
              /** if this group is "fullwidth" (window-width), but the child
               * should only be page width ("variation-1" or "default"), we
               * need to wrap it in a Container. Thankfully, this case is rare.
               *  */
              <ConditionalWrapper
                condition={
                  distanceType === 'fullwidth' &&
                  'field_distance' in item &&
                  (item.field_distance === 'variation-1' || item.field_distance === 'default')
                }
                wrapper={(ch) => <Container>{ch}</Container>}
              >
                <ComponentRecognizer item={item} />
              </ConditionalWrapper>
            )}
          </Grid>
        ))}
      </Grid>
    );
  }

  return <div data-testid={CmsGroupTestIds.noRenderComponentFound} />;
};

// Needed to run this component in Storybook and Jest tests. Register the component in a global componentRegistry object
registerComponents({
  [ComponentTypes.Group]: CmsGroup,
  [ComponentTypes.GroupLocalizable]: CmsGroup,
});
